import React, { FC, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import {
  MuiBox,
  MuiMenuList,
  WarningConfirmationModal,
} from "../../components";
import { MdmOfficeSearchForm } from "./components/mdmOfficeSearchForm/MdmOfficeSearchForm";
import { MdmOfficeSearchResults } from "./components/mdmOfficeSearchResults/MdmOfficeSearchResults";

import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { useSearchClientSavedFiltersMutation } from "../../api/services/client/clientApis";
import {
  MenuListParams,
  SaveSearchFilterParams,
} from "../../api/services/client/models";
import {
  useDeleteMdmOfficeMutation,
  useGetMdmOfficeById,
  useMdmSearchOfficeMutation,
} from "../../api/services/mdmOffice/mdmOfficeApis";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-icon.svg";
import { CommonListModal } from "../../components/commonListModal/CommonListModal";
import { DeleteFilterModal } from "../../components/searchFilters/deleteFilter/DeleteFilter";
import { DeleteMultipleFilterModal } from "../../components/searchFilters/deleteMultipleFilters/DeleteMultipleFilters";
import { OverwriteFilterModal } from "../../components/searchFilters/overwriteFilter/OverwriteFilter";
import { SaveFilterModal } from "../../components/searchFilters/saveFilter/SaveFilter";
import { MAX_SAVE_FILTERS_LIMIT, PAGE_IDS } from "../../helpers/constants";
import {
  isNotEmpty,
  mapFiltersToFormValues,
  trimValue,
} from "../../helpers/utils";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { FilterProps } from "../clientSearch/modelsAndSchemas/Models";
import { MdmCreateOffice } from "../mdmCreateOffice/MdmCreateOffice";
import { mdmOfficeResultSiteCoreKeys } from "../mdmCreateOffice/siteCore/SiteCoreKeys";
import { MdmOfficeSearchHeader } from "./components/mdmOfficeSearchHeader/MdmOfficeSearchHeader";
import MdmOfficeViewModal from "./components/mdmOfficeViewModal/MdmOfficeViewModal";
import {
  OfficeSearchFormDefaultValues,
  OfficeSearchFormKey,
  OfficeSearchFormType,
} from "./modelsAndSchemas/models";
import { SearchMdmOfficeSchema } from "./modelsAndSchemas/ValidationSchema";
import { useEnterKeyAction } from "../../hooks/useEnterKeyAction";

export const pageId = PAGE_IDS.MDM_OFFICE_SEARCH;
const skipTags = ["LI", "SELECT", "TEXTAREA","INPUT"]; // Define the tags to skip for Enter key action

const MdmOfficeSearch: FC = () => {
  const [isSearchClick, setIsSearchClick] = useState(false);
  const [showCreateOfficeModal, setShowCreateOfficeModal] = useState(false);
  const [showDeleteMdmOfficeModal, setShowDeleteMdmOfficeModal] =
    useState(false);
  const [showViewMdmOfficeModal, setShowViewMdmOfficeModal] = useState(false);
  const [selectedOfficeId, setSelectedOfficeId] = useState<string | null>(null);

  const [isOpenSaveFilterModal, setIsOpenSaveFilterModal] = useState(false);
  const [filtersData, setFiltersData] = useState<FilterProps[]>([]);
  const [preloadFilter, setPreloadFilter] = useState(false);
  const [preloadFilterValuesMdmOffice, setPreloadFilterValuesMdmOffice] =
    useState({
      id: "",
      name: "",
      filters: filtersData,
    });
  const [showMultipleDeleteModal, setShowMultipleDeleteModal] = useState(false);
  const [isMaxFilterReached, setIsMaxFilterReached] = useState(false);
  const [showSavedFilter, setShowSavedFilter] = useState(false);
  const [filterToDeleteMdmOffice, setFilterToDeleteMdmOffice] =
    useState<MenuListParams>({ id: "", name: "" });
  const [showModal, setShowModal] = useState(false);

  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const isEditOfficeRef = useRef(false);
  const isViewOfficeRef = useRef(false);

  const { commonValidationMessages, commonLabels, fetchLabel } = useSiteCore();
  const { displayToast } = useToast();
  const dispatch = useDispatch();
  const { isMobile } = useMediaQueries();

  const {
    mutateAsync: deleteMdmOfficeMutation,
    isPending: isDeleteMdmOfficePending,
    isSuccess: isDeleteMdmOfficeSuccess,
  } = useDeleteMdmOfficeMutation();

  const {
    mutateAsync: getMdmSearchOfficeList,
    isPending: isSearchOfficePending,
    data: searchResult,
  } = useMdmSearchOfficeMutation();

  const {
    data: officeData,
    isError: isOfficeError,
    isPending: isOfficeDataPending,
    isSuccess: isOfficeDataSuccess,
    mutate: fetchMdmOfficeById,
  } = useGetMdmOfficeById();

  const {
    data: listOfFiltersOfficeSearch,
    refetch: refetchListOfOfficeSearch,
  } = useSearchClientSavedFiltersMutation({
    searchPageName: "OfficeSearch",
  });

  const methods = useForm<OfficeSearchFormType>({
    mode: "all",
    defaultValues: OfficeSearchFormDefaultValues,
    resolver: yupResolver(SearchMdmOfficeSchema(commonValidationMessages)),
  });

  const { control, handleSubmit } = methods;

  const { reset, getValues } = methods;

  useEffect(() => {
    if (isOfficeDataSuccess) {
      if (isEditOfficeRef.current) {
        setShowCreateOfficeModal(true);
      } else if (isViewOfficeRef.current) {
        setShowViewMdmOfficeModal(true);
      }
    }
  }, [isOfficeDataSuccess]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isGetOfficeDataLoading: {
          isVisible: isOfficeDataPending || isDeleteMdmOfficePending,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ isGetOfficeDataLoading: { isVisible: false } }));
    };
  }, [isOfficeDataPending, isDeleteMdmOfficePending]);

  useEffect(() => {
    if (isOfficeError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
  }, [isOfficeError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isOfficeDataPending ||
          isSearchOfficePending ||
          isDeleteMdmOfficePending,
      }),
    );
  }, [isOfficeDataPending, isSearchOfficePending, isDeleteMdmOfficePending]);

  useEffect(() => {
    if (isDeleteMdmOfficeSuccess) {
      displayToast(
        fetchLabel(
          mdmOfficeResultSiteCoreKeys.officeResultsTitle,
          mdmOfficeResultSiteCoreKeys.officeDeletedMessage,
        ),
        "success",
      );
    }

    getSearchResults();
  }, [isDeleteMdmOfficeSuccess]);

  const ignoredFields = [
    OfficeSearchFormKey.officeNameFilter,
    OfficeSearchFormKey.parentOfficeFilter,
    OfficeSearchFormKey.cityFilter,
    OfficeSearchFormKey.stateFilter,
  ] as string[];

  const formValues = useWatch({ control });

  const isValidForm = Object.entries(formValues).some(
    ([key, value]) => !ignoredFields.includes(key) && value?.trim() !== "",
  );

  const handleSearchClick = async () => {
    setIsSearchClick(true);
    getSearchResults();
  };

  const handleClearAllSearch = () => {
    reset(OfficeSearchFormDefaultValues);
    removeAccountSavedFilters();
    getSearchResults();
  };

  const getSearchResults = () => {
    const {
      city,
      marketId,
      officeName,
      officeNameFilter,
      parentOffice,
      state,
      status,
      parentOfficeFilter,
      cityFilter,
      officeType,
      stateFilter,
      countryId,
    } = getValues();
    const params = {
      officeTypeId: officeType,
      officeName: {
        operatorId: officeNameFilter,
        filter: officeName,
      },
      parentOffice: {
        operatorId: parentOfficeFilter,
        filter: parentOffice,
      },
      statusId: status,
      city: {
        operatorId: cityFilter,
        filter: city,
      },
      state: {
        operatorId: stateFilter,
        filter: state,
      },
      marketId,
      countryId,
    };
    getMdmSearchOfficeList(params);
  };

  const handleDeleteOfficeClick = (officeId: string) => {
    setSelectedOfficeId(officeId);
    setShowDeleteMdmOfficeModal(true);
  };

  const handleEditOfficeClick = (officeId: string) => {
    isEditOfficeRef.current = true;
    isViewOfficeRef.current = false;
    if (showViewMdmOfficeModal) {
      setShowViewMdmOfficeModal(false);
    }
    fetchMdmOfficeById(officeId);
  };

  const handleViewOfficeClick = (officeId: string) => {
    isEditOfficeRef.current = false;
    isViewOfficeRef.current = true;
    fetchMdmOfficeById(officeId);
  };

  const handleConfirmDeleteOffice = () => {
    if (!selectedOfficeId) return;
    setShowDeleteMdmOfficeModal(false);
    deleteMdmOfficeMutation(selectedOfficeId);
    setSelectedOfficeId(null);
  };

  const handleCancelDeleteMdmOffice = () => {
    setShowDeleteMdmOfficeModal(false);
    setSelectedOfficeId(null);
  };

  const handleCloseViewOfficeModal = () => {
    setShowViewMdmOfficeModal(false);
    setSelectedOfficeId(null);
  };

  const handleOnOfficeCreated = (officeId?: string) => {
    handleClearAllSearch();
    if (officeId && isNotEmpty(officeId)) {
      handleViewOfficeClick(officeId);
    }
  };

  const handleCreateOfficeClick = () => {
    isEditOfficeRef.current = false;
    setShowCreateOfficeModal(true);
  };

  const handleCreateOfficeModalClose = () => {
    isEditOfficeRef.current = false;
    setShowCreateOfficeModal(false);
  };

  const closeSaveFilterModal = () => {
    setIsOpenSaveFilterModal(false);
  };

  const onSaveAsNewFilterClickButton = () => {
    setFiltersData([...preloadFilterValuesMdmOffice.filters]);
    if (
      listOfFiltersOfficeSearch &&
      listOfFiltersOfficeSearch.length >= MAX_SAVE_FILTERS_LIMIT
    ) {
      setShowMultipleDeleteModal(true);
      setIsMaxFilterReached(true);
    } else {
      setPreloadFilter(false);
    }
  };

  const handleCloseEvent = () => {
    if (showSavedFilter === true) {
      setShowSavedFilter(false);
    }
  };

  const handleAccountFilterRowClickMdmOffice = (
    item: MenuListParams | SaveSearchFilterParams,
  ) => {
    setPreloadFilter(true);
    const itemFilterMdmOffice = item as SaveSearchFilterParams;
    setPreloadFilterValuesMdmOffice({
      id: itemFilterMdmOffice.id ?? "",
      name: itemFilterMdmOffice.name ?? "",
      filters: mapFiltersToFormValues(
        itemFilterMdmOffice.filters,
        OfficeSearchFormDefaultValues,
        OfficeSearchFormKey,
      ),
    });
    reset(
      mapFiltersToFormValues(
        itemFilterMdmOffice.filters,
        OfficeSearchFormDefaultValues,
        OfficeSearchFormKey,
      ),
    );
    if (showSavedFilter === true) {
      setShowSavedFilter(false);
    }
  };

  const handleDeleteFilterClickMdm = (
    event: React.MouseEvent<HTMLButtonElement>,
    menuItem: MenuListParams,
  ) => {
    event.stopPropagation();
    if (preloadFilterValuesMdmOffice.name === menuItem.name) {
      removeAccountSavedFilters();
    }

    setFilterToDeleteMdmOffice({ id: menuItem?.id, name: menuItem?.name });
    if (showModal === false) {
      setShowModal(true);
    }
  };

  const removeAccountSavedFilters = () => {
    setPreloadFilterValuesMdmOffice({ id: "", name: "", filters: [] });
    setPreloadFilter(false);
  };

  const handleFilterModalClose = () => {
    if (showModal) {
      setShowModal(false);
    }
  };

  const handlePrimaryButtonClick = () => {
    setShowModal(false);
  };

  const handleMultipleFilterDeleteModalClose = () => {
    if (showMultipleDeleteModal) {
      setShowMultipleDeleteModal(false);
    }
  };

  const handleMultipleFilterDeletePrimaryButtonClick = () => {
    setShowMultipleDeleteModal(false);
  };

  const callbackAction = () => {
    if (isMaxFilterReached) {
      setPreloadFilter(false);
    }
    setIsOpenSaveFilterModal(true);
  };

  const handleSetFilterModal = () => {
    setIsOpenSaveFilterModal(!isOpenSaveFilterModal);
  };

  const handleSaveFilterModal = () => {
    if (
      listOfFiltersOfficeSearch &&
      listOfFiltersOfficeSearch.length >= MAX_SAVE_FILTERS_LIMIT &&
      !preloadFilter
    ) {
      if (showMultipleDeleteModal === false) {
        setShowMultipleDeleteModal(true);
      }
    } else {
      handleSetFilterModal();
    }

    const values = getValues();

    const filters: {
      fieldName: keyof OfficeSearchFormType;
      operatorIdKey?: keyof OfficeSearchFormType;
    }[] = [
      {
        fieldName: OfficeSearchFormKey.officeName,
        operatorIdKey: OfficeSearchFormKey.officeNameFilter,
      },
      {
        fieldName: OfficeSearchFormKey.parentOffice,
        operatorIdKey: OfficeSearchFormKey.parentOfficeFilter,
      },
      {
        fieldName: OfficeSearchFormKey.city,
        operatorIdKey: OfficeSearchFormKey.cityFilter,
      },
      {
        fieldName: OfficeSearchFormKey.state,
        operatorIdKey: OfficeSearchFormKey.stateFilter,
      },
      { fieldName: OfficeSearchFormKey.marketId },
      {
        fieldName: OfficeSearchFormKey.officeType,
      },

      { fieldName: OfficeSearchFormKey.status },
    ];

    const mapFormValuesToFilterProps = (
      filters: {
        fieldName: keyof OfficeSearchFormType;
        operatorIdKey?: keyof OfficeSearchFormType;
      }[],
      values: OfficeSearchFormType,
    ): FilterProps[] =>
      filters
        .filter(
          ({ fieldName }) =>
            values[fieldName] && isNotEmpty(String(values[fieldName]).trim()),
        )
        .map(({ fieldName, operatorIdKey }) => ({
          fieldName: fieldName as string,
          fieldValue: trimValue(String(values[fieldName])),
          operatorId: operatorIdKey ? values[operatorIdKey] : "",
          savedSearchFilterDataType: 0,
        }));

    const data: FilterProps[] = mapFormValuesToFilterProps(filters, values);

    if (preloadFilterValuesMdmOffice?.id) {
      setPreloadFilterValuesMdmOffice({
        id: preloadFilterValuesMdmOffice.id,
        name: preloadFilterValuesMdmOffice.name,
        filters: data,
      });
    } else {
      setFiltersData(data);
    }
  };

  const handleSavedFilter = () => {
    if (showSavedFilter === false) {
      setShowSavedFilter(true);
    }
  };

  useEnterKeyAction({
    onEnter: () => {
      if (!showCreateOfficeModal && !showViewMdmOfficeModal) {
        handleSubmit(handleSearchClick)();
      }
    },
    skipTags: skipTags,
  });

  return (
    <MuiBox data-testid="mdm-office-search">
      <MdmOfficeSearchHeader onCreateOffice={handleCreateOfficeClick} />
      <FormProvider {...methods}>
        <MdmOfficeSearchForm
          isValidForm={isValidForm}
          handleSearchClick={handleSubmit(handleSearchClick)}
          handleClearAllSearch={handleClearAllSearch}
          handleSaveFilter={handleSaveFilterModal}
          handleSavedFilter={handleSavedFilter}
          refetchListOfFilters={refetchListOfOfficeSearch}
          anchorRef={anchorRef}
          disableActionBtn={!listOfFiltersOfficeSearch?.length}
        />
      </FormProvider>
      <MdmOfficeSearchResults
        isSearchClick={isSearchClick}
        onDeleteOfficeClick={handleDeleteOfficeClick}
        onEditOfficeClick={handleEditOfficeClick}
        onViewOfficeClick={handleViewOfficeClick}
        rows={searchResult ?? []}
      />
      {showCreateOfficeModal && (
        <MdmCreateOffice
          open={showCreateOfficeModal}
          onClose={handleCreateOfficeModalClose}
          onOfficeCreated={handleOnOfficeCreated}
          isEdit={isEditOfficeRef.current}
          officeData={officeData}
        />
      )}
      {showDeleteMdmOfficeModal && (
        <WarningConfirmationModal
          modalTitle={commonLabels.confirmation}
          show={showDeleteMdmOfficeModal}
          onClose={handleCancelDeleteMdmOffice}
          secondaryButtonTitle={commonLabels.no}
          onPrimaryButtonClick={handleConfirmDeleteOffice}
          labelContent={fetchLabel(
            mdmOfficeResultSiteCoreKeys.officeResultsTitle,
            mdmOfficeResultSiteCoreKeys.areYouSureYouWantToDeleteTheOffice,
          )}
        />
      )}

      {isOpenSaveFilterModal && !preloadFilter && (
        <SaveFilterModal
          showModal={isOpenSaveFilterModal}
          handleModalClose={closeSaveFilterModal}
          pageId={pageId}
          savedSearchPageName={7}
          filters={filtersData}
          listOfFilters={listOfFiltersOfficeSearch}
          refetchListOfFilters={refetchListOfOfficeSearch}
        />
      )}

      {preloadFilter && isOpenSaveFilterModal && (
        <OverwriteFilterModal
          showModal={isOpenSaveFilterModal && preloadFilter}
          handleModalClose={closeSaveFilterModal}
          handleSaveNewFilterClick={onSaveAsNewFilterClickButton}
          overrideFilterValues={preloadFilterValuesMdmOffice}
          refetchListOfFilters={refetchListOfOfficeSearch}
        />
      )}
      {showSavedFilter &&
        listOfFiltersOfficeSearch &&
        (!isMobile ? (
          <MuiMenuList
            menuArray={listOfFiltersOfficeSearch}
            refValue={anchorRef.current}
            open={showSavedFilter}
            onClose={handleCloseEvent}
            onRowClick={handleAccountFilterRowClickMdmOffice}
            onActionBtnClick={handleDeleteFilterClickMdm}
          />
        ) : (
          <CommonListModal
            title={commonLabels.savedFilter}
            open={showSavedFilter}
            onIconButtonClick={handleDeleteFilterClickMdm}
            onFilterClick={handleAccountFilterRowClickMdmOffice}
            buttonIcon={<DeleteIcon />}
            listItems={listOfFiltersOfficeSearch}
            closeHandler={handleCloseEvent}
            secondaryButtonTitle={commonLabels.close}
          />
        ))}

      {showModal && (
        <DeleteFilterModal
          showModal={showModal}
          handleModalClose={handleFilterModalClose}
          handlePrimaryButtonClick={handlePrimaryButtonClick}
          filterToDelete={filterToDeleteMdmOffice}
          refetchListOfFilters={refetchListOfOfficeSearch}
        />
      )}

      {showMultipleDeleteModal && (
        <DeleteMultipleFilterModal
          showModal={showMultipleDeleteModal}
          handleModalClose={handleMultipleFilterDeleteModalClose}
          handlePrimaryButtonClick={
            handleMultipleFilterDeletePrimaryButtonClick
          }
          listOfFilters={listOfFiltersOfficeSearch}
          refetchListOfFilters={refetchListOfOfficeSearch}
          callbackAction={callbackAction}
        />
      )}
      {showViewMdmOfficeModal && officeData && isNotEmpty(officeData) && (
        <MdmOfficeViewModal
          open={showViewMdmOfficeModal}
          onClose={handleCloseViewOfficeModal}
          onOfficeEdit={handleEditOfficeClick}
          data={officeData}
        />
      )}
    </MuiBox>
  );
};

export default MdmOfficeSearch;
